import { useEffect, useState, type ReactNode } from "react";
import { api, errorMessage } from "@/lib/api";
import { useRefreshDirectory } from "@/lib/queries";
import { leverage, money, dateTime } from "@/lib/format";
import type { Account } from "@/lib/types";
import { Auto, Button, ConnectionState, Dash, InlineEdit, useIsAdmin } from "@/components/ui";

/** Un conto come lo mostra la coppia. CID e proxy effettivi solo dove la risposta li porta. */
export type PairAccount = Omit<Account, "effective"> & { effective?: Account["effective"] };

/**
 * La coppia prop/broker come UN oggetto diviso da una cucitura (.pair).
 * Prop sempre a sinistra in indaco, broker sempre a destra in ambra.
 */
export function PairView<A extends PairAccount>({
  prop,
  broker,
  canWrite,
  compact = false,
  onEdit,
  onAdd,
  extra,
}: {
  prop: A | null;
  broker: A | null;
  canWrite: boolean;
  compact?: boolean;
  onEdit?: (a: A) => void;
  onAdd?: (role: "PROP" | "BROKER") => void;
  /** Un segnale in piu' sulla riga del conto (es. la differenza di riconciliazione). */
  extra?: (a: A) => ReactNode;
}) {
  return (
    <div className="pair">
      <Leg side="PROP" account={prop} canWrite={canWrite} compact={compact} onEdit={onEdit} onAdd={onAdd} extra={extra} />
      <Leg side="BROKER" account={broker} canWrite={canWrite} compact={compact} onEdit={onEdit} onAdd={onAdd} extra={extra} />
    </div>
  );
}

function Leg<A extends PairAccount>({
  side,
  account,
  canWrite,
  compact,
  onEdit,
  onAdd,
  extra,
}: {
  side: "PROP" | "BROKER";
  account: A | null;
  canWrite: boolean;
  compact: boolean;
  onEdit?: (a: A) => void;
  onAdd?: (role: "PROP" | "BROKER") => void;
  extra?: (a: A) => ReactNode;
}) {
  const isProp = side === "PROP";
  const sideClass = isProp ? "pair-side-prop" : "pair-side-broker";
  const title = (
    <span className={`text-xs font-medium ${isProp ? "text-prop" : "text-broker"}`}>{isProp ? "Prop" : "Broker"}</span>
  );

  if (!account) {
    return (
      <section className={`${sideClass} flex flex-col items-start gap-2 p-4`}>
        {title}
        <p className="text-sm text-muted">
          {isProp
            ? "Aggiungi il conto prop: è la gamba di partenza di ogni operazione."
            : "Aggiungi il conto broker: riceve la gamba opposta, dimensionata dal moltiplicatore."}
        </p>
        {canWrite && onAdd && (
          <Button size="sm" onClick={() => onAdd(side)}>
            Aggiungi conto {isProp ? "prop" : "broker"}
          </Button>
        )}
      </section>
    );
  }

  return (
    <section className={`${sideClass} min-w-0 p-4`}>
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {title}
          <p className="truncate text-sm font-medium text-ink">{account.label}</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-sm text-muted">
            <span className="machine-id">{account.mt5Login}</span>
            <span className="truncate">{account.server.displayName ?? account.server.name}</span>
            {extra?.(account)}
          </p>
        </div>
        <ConnectionState connected={account.isConnected} error={account.lastConnectError} />
      </header>

      <dl className={`mt-3 grid gap-x-4 gap-y-2 text-sm ${compact ? "grid-cols-3" : "grid-cols-2"}`}>
        <Stat label="Saldo">
          {account.lastBalance === null ? <Dash title="Mai sincronizzato" /> : <Auto>{money(account.lastBalance, account.currency)}</Auto>}
        </Stat>
        <Stat label="Equity">
          {account.lastEquity === null ? <Dash title="Mai sincronizzato" /> : <Auto>{money(account.lastEquity, account.currency)}</Auto>}
        </Stat>
        <Stat label="Leva">
          <LeverageCell account={account} canWrite={canWrite} />
        </Stat>
        {!compact && account.effective && (
          <>
            <Stat label="CID">
              {account.effective.computerId ? (
                <span className="flex flex-col">
                  <span className="machine-id break-all">{account.effective.computerId}</span>
                  <span className="text-xs text-faint">
                    {account.effective.computerIdFromCycle ? "congelato sul ciclo aperto" : "del cliente"}
                  </span>
                </span>
              ) : (
                <span className="text-muted">Nessun CID fisso</span>
              )}
            </Stat>
            <Stat label="Proxy">
              {account.effective.proxy ? (
                <span className="flex flex-col">
                  <span className="text-ink">
                    {account.effective.proxy.host}:{account.effective.proxy.port}
                  </span>
                  <span className="text-xs text-faint">del cliente</span>
                </span>
              ) : (
                <span className={isProp ? "text-loss" : "text-muted"}>
                  {isProp ? "Manca: assegnalo al cliente o il conto non si connette" : "Nessuno: il broker non passa dal proxy"}
                </span>
              )}
            </Stat>
            <Stat label="Ultimo sync">{account.lastSyncAt ? <Auto>{dateTime(account.lastSyncAt)}</Auto> : <Dash />}</Stat>
          </>
        )}
      </dl>

      {compact && (
        <div className="mt-2 text-sm">
          <span className="text-muted">Note conto </span>
          <NotesCell account={account} canWrite={canWrite} />
        </div>
      )}

      {account.lastConnectError && !account.isConnected && <p className="mt-2 text-xs text-loss">{account.lastConnectError}</p>}

      {canWrite && <LegActions account={account} onEdit={onEdit} />}
    </section>
  );
}

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-faint">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}

/** Leva: rilevata in corsivo, override manuale in tondo. Clic per impostarla, vuoto per tornare al rilevato. */
function LeverageCell({ account, canWrite }: { account: PairAccount; canWrite: boolean }) {
  const refresh = useRefreshDirectory();
  const display =
    account.leverageOverride !== null ? (
      <span title="Impostata a mano">{leverage(account.leverageOverride)}</span>
    ) : account.lastLeverage !== null ? (
      <Auto>{leverage(account.lastLeverage)}</Auto>
    ) : (
      <Dash />
    );

  return (
    <InlineEdit
      ariaLabel={`Leva di ${account.label}`}
      display={display}
      disabled={!canWrite}
      inputMode="numeric"
      placeholder="vuoto = rilevata"
      initial={account.leverageOverride !== null ? String(account.leverageOverride) : ""}
      onSave={async (v) => {
        const n = v.replace(/^1:/, "").trim();
        if (n !== "" && !/^\d+$/.test(n)) throw new Error("Scrivi un numero, per esempio 500");
        await api.accounts.update(account.id, { leverageOverride: n === "" ? null : Number(n) });
        await refresh();
      }}
    />
  );
}

export function NotesCell({ account, canWrite }: { account: PairAccount; canWrite: boolean }) {
  const refresh = useRefreshDirectory();
  return (
    <InlineEdit
      ariaLabel={`Note di ${account.label}`}
      display={account.notes ? <span className="text-ink">{account.notes}</span> : <span className="text-faint">{canWrite ? "aggiungi" : "—"}</span>}
      disabled={!canWrite}
      initial={account.notes ?? ""}
      onSave={async (v) => {
        await api.accounts.update(account.id, { notes: v.trim() === "" ? null : v.trim() });
        await refresh();
      }}
    />
  );
}

/**
 * Le azioni di un conto: connetti, rileggi il saldo, modifica, mostra la
 * password.
 *
 * **Esportata**, e non per comodità: la pagina del cliente le mette in una riga
 * compatta invece che sotto la gamba di una coppia. Se le ricopiasse, il giorno
 * in cui si aggiunge un'azione ci sarebbero due elenchi da tenere allineati, e
 * il secondo resterebbe indietro. `inline` cambia solo l'involucro — dove
 * stanno, non che cosa sono.
 */
export function LegActions<A extends PairAccount>({
  account,
  onEdit,
  inline = false,
}: {
  account: A;
  onEdit?: (a: A) => void;
  inline?: boolean;
}) {
  const refresh = useRefreshDirectory();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = (name: string, fn: () => Promise<unknown>) => async () => {
    setBusy(name);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      await refresh();
      setBusy(null);
    }
  };

  const bottoni = (
    <>
      {account.isConnected ? (
        <Button size="sm" disabled={!!busy} onClick={run("disconnect", () => api.accounts.disconnect(account.id))}>
          {busy === "disconnect" ? "Disconnessione…" : "Disconnetti"}
        </Button>
      ) : (
        <Button size="sm" disabled={!!busy || account.role === "NONE"} onClick={run("connect", () => api.accounts.connect(account.id))}>
          {busy === "connect" ? "Connessione…" : "Connetti"}
        </Button>
      )}
      <Button size="sm" variant="ghost" disabled={!!busy || !account.mirrorTraderName} onClick={run("sync", () => api.accounts.sync(account.id))}>
        {busy === "sync" ? "Lettura…" : "Aggiorna saldo"}
      </Button>
      {onEdit && (
        <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => onEdit(account)}>
          Modifica
        </Button>
      )}
      <RevealPassword account={account} />
    </>
  );

  if (inline) {
    return (
      <span className="inline-flex flex-wrap items-center gap-2">
        {bottoni}
        {error && <span className="text-xs text-loss">{error}</span>}
      </span>
    );
  }

  return (
    <div className="mt-3 border-t border-line pt-3">
      <div className="flex flex-wrap gap-2">{bottoni}</div>
      {error && <p className="mt-2 text-xs text-loss">{error}</p>}
    </div>
  );
}

/**
 * ---------------------------------------------------------------------------
 * LA PASSWORD MT5: VENTI SECONDI, E POI FUORI DALLA MEMORIA DAVVERO
 * ---------------------------------------------------------------------------
 *
 * Non basta smettere di disegnarla. Un valore tenuto in uno stato per poterlo
 * rimostrare è **visibile in un dump della pagina** — e chi guarda un dump non
 * è fermato da un `hidden`. Quindi allo scadere lo stato si **azzera**, e
 * riaprire rifà la chiamata.
 *
 * La conseguenza è voluta: **una riga di audit in più ogni volta che si
 * guarda.** È il prezzo giusto — l'audit deve contare le letture, non le
 * aperture di una scheda, e una password tenuta in memoria per risparmiare una
 * chiamata renderebbe la seconda occhiata invisibile.
 *
 * Per la stessa ragione è una **mutazione e non una query**: react-query
 * terrebbe il valore nella sua cache, che è esattamente il posto da cui non
 * deve passare. Niente `localStorage`, niente stato risalito: vive qui e muore
 * qui.
 */
const VISIBILE_MS = 20_000;

function RevealPassword({ account }: { account: PairAccount }) {
  const isAdmin = useIsAdmin();
  const [password, setPassword] = useState<string | null>(null);
  const [restano, setRestano] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiata, setCopiata] = useState(false);

  /*
   * UN SOLO POSTO DA CUI IL VALORE SPARISCE, ed è un timer che conta anche i
   * secondi mostrati: due meccanismi separati — uno che nasconde e uno che
   * scade — sono due meccanismi che un giorno non sono d'accordo, e quello che
   * resta indietro è sempre il secondo.
   */
  useEffect(() => {
    if (password === null) return;
    const scade = Date.now() + VISIBILE_MS;
    setRestano(Math.ceil(VISIBILE_MS / 1000));
    const t = setInterval(() => {
      const left = Math.ceil((scade - Date.now()) / 1000);
      if (left > 0) return setRestano(left);
      setPassword(null);
      setCopiata(false);
      setRestano(0);
    }, 250);
    return () => clearInterval(t);
  }, [password]);

  if (!isAdmin) return null;

  const chiedi = async () => {
    setBusy(true);
    setError(null);
    setCopiata(false);
    try {
      setPassword((await api.accounts.revealPassword(account.id)).password);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (password === null) {
    return (
      <>
        <Button size="sm" variant="ghost" disabled={busy} onClick={chiedi}>
          {busy ? "Lettura…" : "Mostra password"}
        </Button>
        {error && <span className="self-center text-xs text-loss">{error}</span>}
      </>
    );
  }

  return (
    <span className="flex flex-wrap items-center gap-2">
      <code className="machine-id rounded border border-line bg-raised px-2 py-1 text-sm text-ink">{password}</code>
      <Button
        size="sm"
        variant="ghost"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(password);
            setCopiata(true);
          } catch {
            setError("Copia non riuscita: selezionala a mano");
          }
        }}
      >
        {copiata ? "Copiata" : "Copia"}
      </Button>
      <span className="text-xs text-faint">sparisce fra {restano}s</span>
      {error && <span className="text-xs text-loss">{error}</span>}
    </span>
  );
}
